import { Link, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { resetPassword } from '@/lib/auth-client'
import {
  AuthErrorAlert,
  AuthInput,
  AuthLayout,
  AuthSubmitButton,
  PasswordRequirements,
} from '@/features/auth/components'
import { Button } from '@/components/ui/button'
import { generateLinks, generateMetaTags, routeSEO } from '@/lib/seo'

export const Route = createFileRoute('/reset-password')({
  head: () => ({
    meta: generateMetaTags(routeSEO['/reset-password']),
    links: generateLinks(routeSEO['/reset-password']),
  }),
  component: ResetPasswordPage,
})

const ENDPOINT = '/api/auth/reset-password'

function ResetPasswordPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const searchParams =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null

  const passwordRequirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[a-z]/.test(password), text: 'One lowercase letter' },
    { met: /[0-9]/.test(password), text: 'One number' },
  ]

  const passwordsMatch = password === confirmPassword && password.length > 0
  const allRequirementsMet = passwordRequirements.every((req) => req.met)

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    if (!allRequirementsMet) {
      setError('Please ensure your password meets all requirements')
      setIsLoading(false)
      return
    }

    if (!passwordsMatch) {
      setError('Passwords do not match')
      setIsLoading(false)
      return
    }

    try {
      const token = new URLSearchParams(window.location.search).get('token')

      if (!token) {
        setError(
          'Invalid or missing reset token. Please request a new password reset.',
        )
        setIsLoading(false)
        return
      }

      const { error: authError } = await resetPassword({
        newPassword: password,
        token,
      })

      if (authError) {
        setError(authError.message || 'Failed to reset password')
        return
      }

      setIsSuccess(true)
    } catch (err) {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (isSuccess) {
    return (
      <AuthLayout
        endpoint={ENDPOINT}
        title="Password reset successful"
        description="Your password has been updated. You can now sign in with your new password."
      >
        <div className="mb-6 flex items-center gap-3 rounded-2xl border border-[var(--color-success-border)] bg-[var(--color-success-bg)] px-4 py-3">
          <span className="material-symbols-outlined shrink-0 text-lg text-[var(--color-success)]">
            check_circle
          </span>
          <p className="font-mono text-caption text-[var(--color-success-text)]">
            password.updated
          </p>
        </div>

        <Button asChild className="w-full font-semibold tracking-tight">
          <Link to="/sign-in">Sign in to your account</Link>
        </Button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      endpoint={ENDPOINT}
      title="Reset your password"
      description="Enter your new password below. Make sure it's strong and unique."
      footer={
        <Link
          to="/sign-in"
          search={searchParams ? Object.fromEntries(searchParams) : {}}
          className="inline-flex items-center gap-1.5 transition-colors hover:text-[var(--color-text-primary)]"
        >
          <span className="material-symbols-outlined text-base">
            arrow_back
          </span>
          Back to sign in
        </Link>
      }
    >
      <AuthErrorAlert error={error} />

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <AuthInput
            type="password"
            id="password"
            name="password"
            label="New password"
            placeholder="Create a new password"
            icon="lock"
            required
            disabled={isLoading}
            showPasswordToggle
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {password && (
            <div className="mt-3">
              <PasswordRequirements requirements={passwordRequirements} />
            </div>
          )}
        </div>

        <div>
          <AuthInput
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            label="Confirm new password"
            placeholder="Confirm your new password"
            icon="lock"
            required
            disabled={isLoading}
            showPasswordToggle
            error={Boolean(confirmPassword) && !passwordsMatch}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          {confirmPassword && !passwordsMatch && (
            <p className="mt-2 text-body-sm text-[var(--color-error-text)]">
              Passwords do not match
            </p>
          )}
          {confirmPassword && passwordsMatch && (
            <p className="mt-2 flex items-center gap-1 text-body-sm text-[var(--color-success-text)]">
              <span className="material-symbols-outlined text-base">
                check
              </span>
              Passwords match
            </p>
          )}
        </div>

        <AuthSubmitButton
          isLoading={isLoading}
          disabled={!allRequirementsMet || !passwordsMatch}
        >
          Reset password
        </AuthSubmitButton>
      </form>
    </AuthLayout>
  )
}
